import { useCallback } from "react"
import { useMutation } from "urql"

import "../graphql/glTransaction.mutation"

import { type GlTransactionUpdateInput, UpdateGlTransactionDocument } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

export interface UseUpdateGlTransactionReturn {
  updateGlTransaction: (input: GlTransactionUpdateInput) => Promise<boolean>
  isUpdating: boolean
}

export const useUpdateGlTransaction = (): UseUpdateGlTransactionReturn => {
  const [{ fetching }, executeMutation] = useMutation(UpdateGlTransactionDocument)
  const notify = useNotificationsStore((state) => state.enqueueNotification)

  const updateGlTransaction = useCallback(
    async (input: GlTransactionUpdateInput): Promise<boolean> => {
      const result = await executeMutation({ input })

      if (result.error) {
        notify({
          type: NotificationType.Error,
          silent: false,
          message: result.error.message || "Failed to update transaction. Please try again.",
        })
        return false
      }

      const output = result.data?.glTransaction.update

      if (output?.__typename === "GlTransactionFailure") {
        notify({
          type: NotificationType.Error,
          silent: false,
          message: output.error.message || "Failed to update transaction. Please try again.",
        })
        return false
      }

      const referenceNumber =
        output?.__typename === "GlTransactionUpdateSuccess" ? output.glTransaction.referenceNumber : undefined

      notify({
        type: NotificationType.Success,
        silent: false,
        message: `GL Entry updated successfully.${referenceNumber ? ` Reference: ${referenceNumber}` : ""}`,
      })
      return true
    },
    [executeMutation, notify]
  )

  return {
    updateGlTransaction,
    isUpdating: fetching,
  }
}
